import * as React from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import CustomInput from "../../../component/Input/CustomInput";
import CustomButton from "../../../component/Buttons/CustomButton";
import PrivacyPolicyText from "../../../component/PrivacyPolicyText";
import { set_message } from "redux/reducer/message";
import { sendCode } from "api";
import { useDispatch } from "react-redux";
import PhoneNumberInputLogin from "component/Input/PhoneInputLogin";
import { useFormik } from 'formik';
import * as yup from 'yup';
import ReCAPTCHA from "react-google-recaptcha";
import Section from "../home/components/Section";
import { useSelector } from "react-redux";
import {
  isDevTestCredentialsEnabled,
  isTestLoginEmail,
  isTestLoginPhone,
  TEST_LOGIN,
  TEST_OTP,
} from 'constants/testCredentials';
import { clearActiveAccountId } from 'utils/accountContext';
import { clearLocalStoragePreservingWalkthrough } from 'utils/onboardingWalkthrough';
import {
  getVerificationRedirectPath,
  isVerificationRequiredError,
  persistVerificationUser,
} from 'utils/verificationGate';
import { set_user } from 'redux/reducer/user';

const devLoginInitialValues = isDevTestCredentialsEnabled
  ? { ...TEST_LOGIN }
  : { email: '', phone: '', country_code: '+1' };

const validationSchema = yup.object({
  phone: yup.string().max(10,'Maximum 10 digits').min(10,'Min 10 digits').matches(/^[0-9]+$/, 'Numbers only'),
  email:yup.string().email('Invalid email format').max(50,'Max 50 characters')
});



export const Login = () => {
  React.useEffect(()=>{
    document.title='XGain | Login'
    clearLocalStoragePreservingWalkthrough();
    clearActiveAccountId();
  },[])
  const [username, setUsername] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [active, setActive] = React.useState("phone");
  const [captcha,setCaptcha]=React.useState(null);
  const recaptchaRef = React.useRef(null);
  const error=useSelector(state=>state.error.error);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');

  const resetCaptcha = React.useCallback(() => {
    setCaptcha(null);
    recaptchaRef.current?.reset();
  }, []);

  const formik = useFormik({
    initialValues: devLoginInitialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if(active=='phone' && !values.phone){
        formik.setFieldError('phone',' required*')
        return;
      }else if(active=='email' && !values.email){
        formik.setFieldError('email',' required*')
        return
      }
      const skipCaptcha = isTestLoginEmail(values.email)
        || (active === 'phone' && isTestLoginPhone(values.country_code, values.phone));
      if (!skipCaptcha && !captcha) {
        dispatch(set_message('Please check "I\'m not a robot" before logging in.'));
        return;
      }
      let username=active=='phone'?values.country_code+' '+values.phone:values.email
      sendCode(username, captcha || 'dev-bypass')
      .then(() => {
        // dispatch(set_message(`CODE sent to ${username}`))
        navigate('/login/verify', { state: { username: username } })
      })
      .catch((err) => {
        resetCaptcha();
        if (isVerificationRequiredError(err)) {
          const payload = err.response.data;
          persistVerificationUser(payload.user);
          dispatch(set_user(payload.user));
          navigate(getVerificationRedirectPath(payload));
          return;
        }
        if (err.response.status < 500 && err.response.status >= 400) {
          const msg = err.response.data.msg;
          dispatch(set_message(/captcha/i.test(msg)
            ? 'Please check "I\'m not a robot" again, then tap Log In.'
            : msg));
        } else {
          dispatch(set_message('Ooops! Something went wrong!'))
        }
        setLoading(false)
      })
    },
  });

  const handleChange = (e) => setUsername(e.target.value);
 
  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if(!username)return;
  //   if (active=='email') {
  //     var pattern = /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/;
  //     if (!username.match(pattern)) {
  //       dispatch(set_message("Invalid email address"))
  //       return;
  //     }
  //   } else {
  //     let pattern = /^\+\d{1,3}\s\d{10}$/;
  //     if (!username.match(pattern)) {
  //       return dispatch(set_message("Invalid phone number!"));
  //     }

  //   }
  //   setLoading(true)
  //   sendCode(username)
  //     .then(() => {
  //       dispatch(set_message(`CODE sent to ${username}`))
  //       navigate('/login/verify', { state: { username: username } })
  //     })
  //     .catch((err) => {
  //       console.log(err)
  //       if (err.response.status < 500 && err.response.status >= 400) {
  //         console.log(err)
  //         dispatch(set_message(err.response.data.msg))
  //       } else {
  //         dispatch(set_message('Ooops! Something went wrong!'))
  //       }
  //       setLoading(false)
  //     })
  // }

  React.useEffect(() => {
    if (isMobile) return;
    const fieldId = active === 'phone' ? 'phone' : 'email';
    const timer = window.setTimeout(() => {
      document.getElementById(fieldId)?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [active, isMobile]);

  const toggleOptions = [
    { key: 'phone', label: 'Text' },
    { key: 'email', label: 'Email' },
  ];

  return (
    <Section compact sx={{ bgcolor: '#21232E', pb: 4 }}>
      <Box
        component="form"
        className="login-form-dark"
        sx={{
          maxWidth: 400,
          width: '92%',
          color: '#fff',
        }}
        mx="auto"
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Typography
          mt={{ xs: '15%', md: '22%' }}
          sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }}
          variant="h6"
        >
          How should we send your CODE?
        </Typography>

        {isDevTestCredentialsEnabled && (
          <Typography sx={{ color: '#93c5fd', textAlign: 'center', mt: 1, fontSize: 13, lineHeight: 1.5 }}>
            Dev login: {TEST_LOGIN.email} or {TEST_LOGIN.country_code} {TEST_LOGIN.phone} · OTP {TEST_OTP}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2.5 }}>
          <Box
            role="tablist"
            aria-label="Send code via text or email"
            sx={{
              display: 'flex',
              bgcolor: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '999px',
              p: '4px',
              width: '100%',
              maxWidth: 280,
            }}
          >
            {toggleOptions.map(({ key, label }) => {
              const selected = active === key;
              return (
                <Button
                  key={key}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActive(key)}
                  disableElevation
                  sx={{
                    flex: 1,
                    borderRadius: '999px',
                    py: 1,
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    transition: 'all 0.2s ease',
                    ...(selected
                      ? {
                          bgcolor: '#95F205',
                          color: '#000',
                          boxShadow: '0 2px 10px rgba(149, 242, 5, 0.4)',
                          '&:hover': { bgcolor: '#a8ff0a' },
                        }
                      : {
                          bgcolor: 'transparent',
                          color: 'rgba(255,255,255,0.5)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.06)',
                            color: 'rgba(255,255,255,0.85)',
                          },
                        }),
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box mt={2}>
          <Typography sx={{ color: '#f87171', textAlign: 'center' }}>{error}</Typography>
          {active === 'phone' ? (
            <PhoneNumberInputLogin
              dark
              id="phone"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              formik={formik}
              handleSubmit={formik.handleSubmit}
            />
          ) : (
            <CustomInput
              dark
              id="email"
              type="email"
              placeHolder="Email address"
              name="email"
              value={formik.values.email}
              autoComplete="email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          )}
        </Box>
        <Box my={3} textAlign="center">
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Don&apos;t have an account?
            <Link to="/register" style={{ marginLeft: 4, color: '#60a5fa', textDecoration: 'none' }}>
              Join Now
            </Link>
          </Typography>
        </Box>

        <Box
          className={`login-recaptcha-wrap ${isMobile ? 'login-recaptcha-wrap--compact' : 'login-recaptcha-wrap--normal'}`}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Ld8rWknAAAAAD_jifcFUozxmQK79tSd4J4V9C2p"
            theme="dark"
            size={isMobile ? 'compact' : 'normal'}
            onChange={setCaptcha}
            onExpired={resetCaptcha}
          />
        </Box>

        {(captcha
          || (active === 'email' && isTestLoginEmail(formik.values.email))
          || (active === 'phone' && isTestLoginPhone(formik.values.country_code, formik.values.phone))
        ) && <CustomButton loading={loading} type="submit" text="Log In" />}
        <Box sx={{ color: 'rgba(255,255,255,0.75)', '& a': { color: '#60a5fa' } }}>
          <PrivacyPolicyText />
        </Box>
      </Box>
    </Section>
  );
};

export default Login;
